import React, { useRef, useState } from "react";
import { uploadMedia, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Camera, Mic, Video, X, Image as ImageIcon, Play, Loader2,
  Paperclip, FolderOpen, FileAudio,
} from "lucide-react";

// Single attachment — { kind: "image"|"audio"|"video", url, name, size, content_type }
const KIND_META = {
  image: { icon: ImageIcon, color: "#22D3EE", label: "Photo" },
  audio: { icon: Mic,       color: "#A78BFA", label: "Audio" },
  video: { icon: Video,     color: "#F97316", label: "Vidéo" },
};

const fmtSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
};

const AttachmentThumb = ({ att, onRemove }) => {
  const meta = KIND_META[att.kind] || KIND_META.image;
  const Icon = meta.icon;
  const url = mediaUrl(att.url);
  return (
    <div
      className="relative shrink-0 w-20 h-20 border-2 border-rescue-line bg-rescue-bg overflow-hidden group"
      data-testid={`attachment-thumb-${att.kind}`}
    >
      {att.kind === "image" && (
        <img src={url} alt={att.name || "photo"} className="w-full h-full object-cover" />
      )}
      {att.kind === "video" && (
        <div className="w-full h-full flex items-center justify-center bg-black text-white">
          <Play className="w-6 h-6" />
          <video src={url} className="absolute inset-0 w-full h-full object-cover opacity-60" muted />
        </div>
      )}
      {att.kind === "audio" && (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-1 text-center" style={{ background: meta.color }}>
          <Icon className="w-6 h-6 text-rescue-ink" strokeWidth={2.5} />
          <span className="text-[9px] font-bold text-rescue-ink">{fmtSize(att.size)}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        data-testid={`attachment-remove-${att.kind}`}
        className="absolute top-0 right-0 w-5 h-5 flex items-center justify-center bg-rescue-red text-white opacity-90 hover:opacity-100"
        aria-label="Retirer"
      >
        <X className="w-3 h-3" strokeWidth={3} />
      </button>
      <div className="absolute bottom-0 inset-x-0 bg-rescue-ink/90 text-white text-[8px] font-bold uppercase tracking-[0.15em] px-1 py-0.5 flex items-center justify-between">
        <span className="flex items-center gap-1"><Icon className="w-2.5 h-2.5" />{meta.label}</span>
      </div>
    </div>
  );
};

export const AttachmentPicker = ({
  attachments,
  onChange,
  testidPrefix = "attach",
  // Which kinds to allow (always defaults to all three)
  allowedKinds = ["image", "audio", "video"],
  max = 6,
}) => {
  // Two refs per visual kind: one for the OS camera (capture="environment")
  // and one for the gallery/file picker (no capture attribute → device shows
  // its native file browser so users can attach photos/videos/audio that
  // were taken or downloaded *before* this session).
  const camImageRef = useRef(null);
  const gallImageRef = useRef(null);
  const camVideoRef = useRef(null);
  const gallVideoRef = useRef(null);
  const gallAudioRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastError, setLastError] = useState(null); // {message, file} — surfaced inline for retry

  // Audio recording state — uses MediaRecorder API
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Iter66 — Map HTTP status codes to French operator-friendly messages
  // so the citizen sees a clear reason instead of "Échec de l'envoi".
  const _errMessage = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "string") return detail;
    if (status === 413) return "Fichier trop volumineux. Réduisez-le ou prenez une photo de qualité moyenne.";
    if (status === 415) return "Type de fichier non pris en charge.";
    if (status === 400) return "Fichier vide ou invalide.";
    if (status === 401 || status === 403) return "Accès refusé.";
    if (err?.message === "Network Error" || !status) return "Réseau faible — téléversement échoué. Réessayez.";
    return "Échec du téléversement.";
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (attachments.length >= max) {
      toast.error(`Maximum ${max} pièces jointes`);
      return;
    }
    setBusy(true); setProgress(0); setLastError(null);
    try {
      const meta = await uploadMedia(file, setProgress);
      onChange([...attachments, meta]);
      toast.success(`${KIND_META[meta.kind]?.label || "Fichier"} ajouté`);
    } catch (e) {
      const msg = _errMessage(e);
      setLastError({ message: msg, file });
      toast.error(msg);
    } finally { setBusy(false); setProgress(0); }
  };

  const retryLastUpload = () => {
    if (lastError?.file) handleUpload(lastError.file);
  };

  const startAudio = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const file = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type });
        await handleUpload(file);
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      console.error("audio capture failed", e);
      toast.error("Micro indisponible — autorisez l'accès au micro.");
    }
  };
  const stopAudio = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
  };

  const onRemove = (i) => onChange(attachments.filter((_, idx) => idx !== i));

  const canImage = allowedKinds.includes("image");
  const canAudio = allowedKinds.includes("audio");
  const canVideo = allowedKinds.includes("video");
  const reachedMax = attachments.length >= max;

  // Shared tile button factory — keeps the colorful per-kind styling but
  // drops the redundant label so the row reads compactly on a 390 px phone.
  const tileClass = (color) =>
    `flex flex-col items-center justify-center gap-1 py-2.5 border-2 transition-colors disabled:opacity-40 ${color}`;

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-picker`}>
      <div className="flex items-center gap-2">
        <Paperclip className="w-3.5 h-3.5 text-rescue-mute" strokeWidth={2.5} />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-mute">
          Pièces jointes <span className="text-rescue-ink">{attachments.length}/{max}</span>
        </span>
        {busy && (
          <span
            data-testid={`${testidPrefix}-upload-progress`}
            className="text-[10px] font-mono text-rescue-ink flex items-center gap-1.5 ml-auto"
          >
            <Loader2 className="w-3 h-3 animate-spin" />
            Téléversement en cours · {progress}%
          </span>
        )}
      </div>

      {/* Iter66 — clear failure banner with one-tap retry */}
      {!busy && lastError && (
        <div
          data-testid={`${testidPrefix}-upload-error`}
          className="border-2 border-rose-400 bg-rose-50 px-2 py-1.5 flex items-center gap-2 text-[10px] text-rose-700"
        >
          <span className="flex-1 font-bold">{lastError.message}</span>
          <button
            type="button"
            onClick={retryLastUpload}
            data-testid={`${testidPrefix}-upload-retry`}
            className="font-bold uppercase tracking-[0.15em] px-2 py-1 border border-rose-600 hover:bg-rose-100"
          >
            Réessayer
          </button>
          <button
            type="button"
            onClick={() => setLastError(null)}
            className="px-1 py-1 hover:bg-rose-100"
            aria-label="Ignorer"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testidPrefix}-list`}>
          {attachments.map((a, i) => (
            <AttachmentThumb key={`${a.url}-${i}`} att={a} onRemove={() => onRemove(i)} />
          ))}
        </div>
      )}

      {/* PHOTO row — capture new OR pick from gallery. Tiny "Galerie" link
          opens the OS file browser without forcing the camera. */}
      {canImage && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-rescue-mute">Photo</span>
            <button
              type="button"
              onClick={() => gallImageRef.current?.click()}
              disabled={busy || reachedMax}
              data-testid={`${testidPrefix}-pick-image-from-gallery`}
              className="text-[10px] font-bold text-cyan-700 dark:text-cyan-300 inline-flex items-center gap-1 hover:underline disabled:opacity-40"
            >
              <FolderOpen className="w-3 h-3" /> Galerie
            </button>
          </div>
          <input
            ref={camImageRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid={`${testidPrefix}-image-input`}
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <input
            ref={gallImageRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testidPrefix}-image-gallery-input`}
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <button
            type="button"
            disabled={busy || reachedMax}
            onClick={() => camImageRef.current?.click()}
            data-testid={`${testidPrefix}-add-image`}
            className={tileClass("w-full border-cyan-400 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 dark:bg-cyan-950/40 dark:text-cyan-200")}
          >
            <Camera className="w-4 h-4" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Prendre une photo</span>
          </button>
        </div>
      )}

      {/* AUDIO row — record live OR attach an existing audio file from disk. */}
      {canAudio && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-rescue-mute">Audio</span>
            <button
              type="button"
              onClick={() => gallAudioRef.current?.click()}
              disabled={busy || reachedMax || recording}
              data-testid={`${testidPrefix}-pick-audio-from-gallery`}
              className="text-[10px] font-bold text-violet-700 dark:text-violet-300 inline-flex items-center gap-1 hover:underline disabled:opacity-40"
            >
              <FileAudio className="w-3 h-3" /> Fichier
            </button>
          </div>
          <input
            ref={gallAudioRef}
            type="file"
            accept="audio/*"
            className="hidden"
            data-testid={`${testidPrefix}-audio-gallery-input`}
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <button
            type="button"
            disabled={busy || (!recording && reachedMax)}
            onClick={recording ? stopAudio : startAudio}
            data-testid={`${testidPrefix}-${recording ? "stop" : "add"}-audio`}
            className={`w-full ${tileClass(
              recording
                ? "border-rescue-red bg-rescue-red text-white animate-pulse"
                : "border-violet-400 text-violet-700 bg-violet-50 hover:bg-violet-100 dark:bg-violet-950/40 dark:text-violet-200"
            )}`}
          >
            <Mic className="w-4 h-4" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]">
              {recording ? "Arrêter l'enregistrement" : "Enregistrer audio"}
            </span>
          </button>
        </div>
      )}

      {/* VIDEO row — capture new OR pick existing. */}
      {canVideo && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-rescue-mute">Vidéo</span>
            <button
              type="button"
              onClick={() => gallVideoRef.current?.click()}
              disabled={busy || reachedMax}
              data-testid={`${testidPrefix}-pick-video-from-gallery`}
              className="text-[10px] font-bold text-orange-700 dark:text-orange-300 inline-flex items-center gap-1 hover:underline disabled:opacity-40"
            >
              <FolderOpen className="w-3 h-3" /> Galerie
            </button>
          </div>
          <input
            ref={camVideoRef}
            type="file"
            accept="video/*"
            capture="environment"
            className="hidden"
            data-testid={`${testidPrefix}-video-input`}
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <input
            ref={gallVideoRef}
            type="file"
            accept="video/*"
            className="hidden"
            data-testid={`${testidPrefix}-video-gallery-input`}
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <button
            type="button"
            disabled={busy || reachedMax}
            onClick={() => camVideoRef.current?.click()}
            data-testid={`${testidPrefix}-add-video`}
            className={tileClass("w-full border-orange-400 text-orange-700 bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/40 dark:text-orange-200")}
          >
            <Video className="w-4 h-4" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Filmer une vidéo</span>
          </button>
        </div>
      )}
    </div>
  );
};
